<template>
	<v-dialog v-model="value" max-width="70%" scrollable persistent>
		<v-card>
			<v-card-title class="title primary--text mb-5 secondary">
				<translate>Welcome to Naltio</translate>!
			</v-card-title>
			<v-card-text>
				<v-carousel
					v-model="welcomeCarousel"
					:show-arrows="false"
					:height="welcomeCarousel == 1 && student ? 350 : 280"
					light
					hide-delimiters
				>
					<v-carousel-item>
						<p class="text-subtitle-1" v-translate>
							The free plan was activated during a trial period. Select the desired module(s). Later, you can activate and deactivate any module in the "Subscriptions" tab.
						</p>
						<v-item-group mandatory v-model="modules" multiple>
							<v-row no-gutters justify="center" style="height: 200px">
								<v-col cols="3" class="px-1">
									<v-row no-gutters align="center" style="height: 100%">
										<v-col cols="12">
											<v-item :value="2" v-slot:default="{ active, toggle }">
												<v-card
													:color="active ? 'secondary' : ''"
													@click="toggle"
                          shaped
												>
													<v-card-title>
														<translate>TCM</translate>
													</v-card-title>
												</v-card>
											</v-item>
										</v-col>
									</v-row>
								</v-col>
								<v-col cols="3" class="px-1">
									<v-row no-gutters align="center" style="height: 100%">
										<v-col cols="12">
											<v-item :value="1" v-slot:default="{ active, toggle }">
												<v-card
													:color="active ? 'secondary' : ''"
													@click="toggle"
                          shaped
												>
													<v-card-title>
														<translate>Fitotherapy</translate>
													</v-card-title>
												</v-card>
											</v-item>
										</v-col>
									</v-row>
								</v-col>
								<v-col cols="3" class="px-1">
									<v-row no-gutters align="center" style="height: 100%">
										<v-col cols="12">
											<v-item :value="3" v-slot:default="{ active, toggle }">
												<v-card
													:color="active ? 'secondary' : ''"
													@click="toggle"
                          shaped
												>
													<v-card-title>
														<translate>Osteopathy</translate>
													</v-card-title>
												</v-card>
											</v-item>
										</v-col>
									</v-row>
								</v-col>
								<v-col cols="3" class="px-1">
									<v-row no-gutters align="center" style="height: 100%">
										<v-col cols="12">
											<v-item :value="4" v-slot:default="{ active, toggle }">
												<v-card
													:color="active ? 'secondary' : ''"
													@click="toggle"
                          shaped
												>
													<v-card-title>
														<translate>Generic</translate>
													</v-card-title>
												</v-card>
											</v-item>
										</v-col>
									</v-row>
								</v-col>
							</v-row>
						</v-item-group>
					</v-carousel-item>
					<v-carousel-item>
						<p class="text-subtitle-1 primary--text" v-translate>Personal Data</p>
						<v-form ref="personalDataForm" lazy-validation>
							<v-row no-gutters>
								<v-text-field
									v-model="userName"
									:label="$gettext('Name')"
									:rules="nameRules"
									filled
									validate-on-blur
									placeholder=" "
									class="px-1"
								/>
								<v-text-field
									v-model="$store.getters.userEmail"
									:label="$gettext('Email')"
									filled
									disabled
									class="px-1"
								/>
							</v-row>
							<v-row no-gutters v-if="!hasSpeciality">
								<v-checkbox
									v-model="healthProfessional"
									@change="student = false"
									class="mt-0 pt-0 ml-2"
									color="primary"
									:label="$gettext('Health Professional')"
									hide-details
									dense
									:rules="required"
								/>
								<v-checkbox
									v-model="student"
									@change="healthProfessional = false"
									class="mt-0 pt-0 ml-2"
									color="primary"
									:label="$gettext('Student')"
									hide-details
									dense
									:rules="required"
								/>
							</v-row>
							<v-row no-gutters class="mt-4" v-if="!hasSpeciality">
								<v-text-field
									class="pr-2"
									v-if="healthProfessional || student"
									v-model="specialty"
									:label="healthProfessional ? $gettext('Specialty') : $gettext('Education Field')"
									:rules="specialtyRules"
									filled
									validate-on-blur
									placeholder=" "
								/>
								<v-text-field
									v-if="healthProfessional"
									v-model="licenseNumber"
									:label="$gettext('License Number')"
									filled
									placeholder=" "
								/>
								<v-text-field
									class="pl-2"
									v-if="healthProfessional"
									v-model="establishmentName"
									:label="$gettext('Establishment Name')"
									filled
									placeholder=" "
									:rules="establishmentRules"
								/>
								<v-text-field
									v-if="student"
									v-model="school"
									:label="$gettext('School')"
									filled
									placeholder=" "
									:rules="schoolRules"
								/>
								<v-text-field
									v-if="student"
									v-model="studentNumber"
									class="pl-2"
									:label="$gettext('Student Number')"
									filled
									placeholder=" "
								/>
							</v-row>
							<v-row no-gutters v-if="student">
								<v-col cols="6">
									<p class="ma-0">
										<span class="font-weight-bold" v-translate>Note</span>:
										<span v-translate>To have access to all the privileges of the student plan you will need to provide proof of enrollment. This can be done later in "Settings" - "Administrator".</span>
									</p>
								</v-col>
								<v-col cols="6">
									<VueFileAgent
										:theme="'list'"
                    :multiple="false"
                    :deletable="true"
                    :meta="true"
                    :accept="'image/*,.pdf'"
                    :maxSize="'50MB'"
                    :helpText="$gettext('Drag or click to add proof of enrollment')"
                    :errorText="{
                      type: 'Invalid file type',
                      size: 'Files cannot exceed 50MB in size'
                    }"
										@select="studentProof = $event[0]"
                    @beforedelete="studentProof = null"
                    v-model="studentProof"
                  ></VueFileAgent>
								</v-col>
							</v-row>
						</v-form>
					</v-carousel-item>
					<v-carousel-item>
						<p class="text-subtitle-1 text-center" v-translate>
              Your Naltio account has been set up and is ready to be used. In the settings you can edit and learn about other features.
						</p>
            <v-row no-gutters justify="center">
              <Logo width="200" height="200" />
            </v-row>
						<p class="text-subtitle-1 text-center" v-translate>
							You can now do a tutorial in order to be more familiarized with Naltio!
						</p>
					</v-carousel-item>
				</v-carousel>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-btn
					v-if="welcomeCarousel > 0"
					color="primary darken-1"
					@click="welcomeCarousel -= 1"
					text
				>
					<translate>Previous</translate>
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn
					v-if="welcomeCarousel != 2"
					color="primary darken-1"
					key="next"
					text
					@click="nextWelcome"
				>
					<translate>Next</translate>
				</v-btn>
				<template v-else>
					<v-btn
						color="primary darken-1"
						key="finish"
						text
						@click="finishWelcome(false)"
						:loading="loading"
					>
						<translate>Finish</translate>
					</v-btn>
					<v-btn
						color="primary darken-1"
						key="tutorial"
						text
						@click="finishWelcome(true)"
						:loading="loading"
					>
						<translate>Start Tutorial</translate>
					</v-btn>
				</template>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import Logo from "@/assets/logos/Logo.vue";
import axios from "axios";

export default {
  name: "WelcomeTour",
  props: ["value"],
  components: {
    Logo
  },
  data: () => ({
    userName: "",
    welcomeCarousel: 0,
		modules: [2],
		healthProfessional: false,
		student: false,
		specialty: "",
		licenseNumber: "",
		establishmentName: "",
    school: "",
		studentNumber: "",
		studentProof: null,
    hasSpeciality: false,
    loading: false
  }),
  computed: {
    nameRules() {
			return [v => !!v || this.$gettext("Name is required")];
		},
    specialtyRules() {
			return [
				v => !!v ||
					this.$gettext("Specialty is required")
			];
		},
		establishmentRules() {
			return [v => !!v || this.$gettext("Establishment is required")];
		},
		required() {
			return [() => (this.student || this.healthProfessional)  || this.$gettext("Required")];
		},
		schoolRules() {
			return [v => !!v || this.$gettext("Required")];
		}
  },
  methods: {
    nextWelcome() {
			if(this.welcomeCarousel == 1) {
				if(this.$refs.personalDataForm.validate()) this.welcomeCarousel = 2;
			} else this.welcomeCarousel += 1;
		},
		finishWelcome(doTutorial) {
      this.loading = true;
      let form = new FormData();
      form.append('module_ids', JSON.stringify(this.modules));
      form.append('name', this.userName);
      if(!this.hasSpeciality) {
        if(this.healthProfessional) {
          form.append('health_professional', true);
					form.append('license_number', this.licenseNumber);
					form.append('establishment_name', this.establishmentName);
        }
        form.append('student', this.student);
        form.append('school', this.school);
				form.append('student_number', this.studentNumber);
				if(this.studentProof) form.append("student_proof", this.studentProof.file);
        if(this.healthProfessional || this.student) form.append('specialty', this.specialty);
      }
			axios
				.post("/welcome-tour", form)
				.then(response => {
          console.log(response.data);
          if(response.data.result == 'ok') {
            this.$store.dispatch("UPDATE_USER", response.data.user);
          }
					if(doTutorial) this.$router.push({name: "home"}).then(this.$store.dispatch('BEGIN_TOUR'));
					else this.$router.push({name: "home"});
          this.$emit('input', false);
          this.loading = false;
				})
				.catch(error => {
          console.log(error);
          this.loading = false;
        });
		},
  },
  mounted() {
    this.userName = this.$store.getters.userName;
		if(this.$store.getters.hasSpeciality) this.hasSpeciality = true;
		//this.establishmentName = this.$store.getters.spaceName;
  }
}
</script>